import { FunctionComponent, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Modal, Icon } from "@/components";
import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import {
	useGetPipedriveFieldMappingsQuery,
	useGetPipedriveLeadFieldsQuery,
	useGetPipedrivePersonFieldsQuery,
	useGetPipedriveOrganizationFieldsQuery,
	useGetPipedriveDealFieldsQuery,
	useGetPipedriveSourceFieldsQuery,
	useCreatePipedriveFieldMappingMutation,
	useUpdatePipedriveFieldMappingMutation,
	useDeletePipedriveFieldMappingMutation,
	useTestPipedriveAiFieldMappingMutation,
} from "@/pages/Private/redux/pipedrive/pipedrive.api";
import { useLazyGetContactsQuery } from "@/pages/Private/redux/contact/contact.api";

import {
	PipedriveEntityType,
	PipedriveField,
	PipedriveSourceField,
	FieldMappingSourceType,
	PipedriveFieldMapping,
	PIPEDRIVE_DEAL_RESERVED_FIELD_KEYS,
	FieldMappingAiConfig,
} from "../schema/pipedrive";
import {
	PipedriveMappingRow,
	PipedriveNewMappingRow,
	NewPipedriveMapping,
} from "./PipedriveMappingRow";
import { PipedriveAddDefaultsButton } from "./PipedriveAddDefaultsButton";
import {
	AiConfigModal,
	buildAiConfigLabels,
	buildAiProspectOptions,
	FieldMappingFooter,
} from "./fieldMapping";

enum Tabs {
	LEADS = "LEADS",
	PERSONS = "PERSONS",
	ORGANIZATIONS = "ORGANIZATIONS",
	DEALS = "DEALS",
}

interface PipedriveFieldMappingModalProps {
	isOpen: boolean;
	onClose: () => void;
}

interface PipedriveAiConfigSelection {
	id?: number;
	tempId?: string;
	label: string;
	targetField: PipedriveField;
	allFields: PipedriveField[];
	entityType: PipedriveEntityType;
	aiConfig?: FieldMappingAiConfig;
}

const TAB_ENTITY_MAP: Record<Tabs, PipedriveEntityType> = {
	[Tabs.LEADS]: PipedriveEntityType.LEAD,
	[Tabs.PERSONS]: PipedriveEntityType.PERSON,
	[Tabs.ORGANIZATIONS]: PipedriveEntityType.ORGANIZATION,
	[Tabs.DEALS]: PipedriveEntityType.DEAL,
};

export const PipedriveFieldMappingModal: FunctionComponent<PipedriveFieldMappingModalProps> = ({
	isOpen,
	onClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>): string =>
		t(`company.pipedrive.fieldMapping.${key}`, options as never) as unknown as string;

	const [currentTab, setCurrentTab] = useState<Tabs>(Tabs.LEADS);
	const [newMappings, setNewMappings] = useState<NewPipedriveMapping[]>([]);
	const [savingTempId, setSavingTempId] = useState<string | null>(null);
	const [aiConfigTarget, setAiConfigTarget] = useState<PipedriveAiConfigSelection | null>(null);
	const aiProspectSearchRequestRef = useRef<{ abort?: () => void } | null>(null);

	const entityType = TAB_ENTITY_MAP[currentTab];

	const { data: leadMappings, isLoading: isLoadingLeads } = useGetPipedriveFieldMappingsQuery({
		entityType: PipedriveEntityType.LEAD,
	});
	const { data: personMappings, isLoading: isLoadingPersons } = useGetPipedriveFieldMappingsQuery({
		entityType: PipedriveEntityType.PERSON,
	});
	const { data: organizationMappings, isLoading: isLoadingOrganizations } =
		useGetPipedriveFieldMappingsQuery({ entityType: PipedriveEntityType.ORGANIZATION });
	const { data: dealMappings, isLoading: isLoadingDeals } = useGetPipedriveFieldMappingsQuery({
		entityType: PipedriveEntityType.DEAL,
	});

	const { data: leadFields } = useGetPipedriveLeadFieldsQuery();
	const { data: personFields } = useGetPipedrivePersonFieldsQuery();
	const { data: organizationFields } = useGetPipedriveOrganizationFieldsQuery();
	const { data: dealFields } = useGetPipedriveDealFieldsQuery();
	const { data: sourceFields } = useGetPipedriveSourceFieldsQuery();

	const [createMapping] = useCreatePipedriveFieldMappingMutation();
	const [updateMapping] = useUpdatePipedriveFieldMappingMutation();
	const [deleteMapping] = useDeletePipedriveFieldMappingMutation();
	const [testAiMapping] = useTestPipedriveAiFieldMappingMutation();
	const [searchProspects] = useLazyGetContactsQuery();

	const isLoading = isLoadingLeads || isLoadingPersons || isLoadingOrganizations || isLoadingDeals;

	const getMappingsForTab = (tab: Tabs): PipedriveFieldMapping[] | undefined => {
		switch (tab) {
			case Tabs.LEADS:
				return leadMappings;
			case Tabs.PERSONS:
				return personMappings;
			case Tabs.ORGANIZATIONS:
				return organizationMappings;
			case Tabs.DEALS:
				return dealMappings;
		}
	};

	const getPipedriveFieldsForTab = (tab: Tabs): PipedriveField[] => {
		switch (tab) {
			case Tabs.LEADS:
				return leadFields?.fields || [];
			case Tabs.PERSONS:
				return personFields?.fields || [];
			case Tabs.ORGANIZATIONS:
				return organizationFields?.fields || [];
			case Tabs.DEALS:
				return (dealFields?.fields || []).filter(
					(f) => !PIPEDRIVE_DEAL_RESERVED_FIELD_KEYS.includes(f.name)
				);
		}
	};

	const getSourceFieldsForTab = (tab: Tabs): PipedriveSourceField[] => {
		if (tab === Tabs.LEADS) {
			return sourceFields?.leadFields || sourceFields?.personFields || [];
		}

		if (tab === Tabs.ORGANIZATIONS) {
			return sourceFields?.organizationFields || [];
		}

		if (tab === Tabs.DEALS) {
			return sourceFields?.dealFields || sourceFields?.personFields || [];
		}

		return sourceFields?.personFields || [];
	};

	const isFieldMode = (sourceType?: FieldMappingSourceType) =>
		(sourceType ?? FieldMappingSourceType.FIELD) === FieldMappingSourceType.FIELD;

	const usedSourceFields = useMemo(() => {
		const mappings = getMappingsForTab(currentTab);
		const saved =
			mappings
				?.filter((m) => isFieldMode(m.sourceType) && m.sourceField)
				.map((m) => m.sourceField) || [];
		const unsaved = newMappings
			.filter(
				(m) =>
					m.entityType === TAB_ENTITY_MAP[currentTab] && isFieldMode(m.sourceType) && m.sourceField
			)
			.map((m) => m.sourceField);

		return [...saved, ...unsaved];
	}, [currentTab, leadMappings, personMappings, organizationMappings, dealMappings, newMappings]);

	const usedPipedriveFieldsByType = useMemo<Record<FieldMappingSourceType, string[]>>(() => {
		const mappings = getMappingsForTab(currentTab);
		const byType: Record<FieldMappingSourceType, string[]> = {
			[FieldMappingSourceType.FIELD]: [],
			[FieldMappingSourceType.CONSTANT]: [],
			[FieldMappingSourceType.AI]: [],
		};

		mappings
			?.filter((m) => m.pipedriveField)
			.forEach((m) => byType[m.sourceType ?? FieldMappingSourceType.FIELD].push(m.pipedriveField));
		newMappings
			.filter((m) => m.entityType === TAB_ENTITY_MAP[currentTab] && m.pipedriveField)
			.forEach((m) => byType[m.sourceType].push(m.pipedriveField));

		return byType;
	}, [currentTab, leadMappings, personMappings, organizationMappings, dealMappings, newMappings]);

	const handleAddMapping = () => {
		const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

		setNewMappings((prev) => [
			...prev,
			{
				tempId,
				sourceField: "",
				sourceType: FieldMappingSourceType.FIELD,
				pipedriveField: "",
				isActive: true,
				entityType,
			},
		]);
	};

	const handleUpdateNewMapping = (tempId: string, data: Partial<NewPipedriveMapping>) => {
		setNewMappings((prev) => prev.map((m) => (m.tempId === tempId ? { ...m, ...data } : m)));
	};

	const handleConfigureAi = (
		mapping: {
			id?: number;
			tempId?: string;
			pipedriveField: string;
			entityType: PipedriveEntityType | string;
			aiConfig?: FieldMappingAiConfig;
		},
		fields: PipedriveField[]
	) => {
		const target = fields.find((f) => f.name === mapping.pipedriveField) ?? {
			name: mapping.pipedriveField,
			label: mapping.pipedriveField,
			type: "string",
		};

		setAiConfigTarget({
			id: mapping.id,
			tempId: mapping.tempId,
			label: target.label,
			targetField: target,
			allFields: fields,
			entityType: mapping.entityType as PipedriveEntityType,
			aiConfig: mapping.aiConfig,
		});
	};

	const handleSearchAiProspects = async (query: string) => {
		aiProspectSearchRequestRef.current?.abort?.();
		const request = searchProspects({ page: 1, limit: 10, searchValue: query });

		aiProspectSearchRequestRef.current = request;

		try {
			const result = await request.unwrap();

			return buildAiProspectOptions(result.data);
		} catch {
			return [];
		} finally {
			if (aiProspectSearchRequestRef.current === request) {
				aiProspectSearchRequestRef.current = null;
			}
		}
	};

	const handleCancelAiProspectSearch = () => {
		aiProspectSearchRequestRef.current?.abort?.();
		aiProspectSearchRequestRef.current = null;
	};

	const handleSaveAiConfig = async (config: FieldMappingAiConfig) => {
		if (!aiConfigTarget) {
			return;
		}

		if (aiConfigTarget.tempId) {
			handleUpdateNewMapping(aiConfigTarget.tempId, {
				aiConfig: config,
				sourceField: aiConfigTarget.label,
			});
		} else if (aiConfigTarget.id) {
			await updateMapping({
				id: aiConfigTarget.id,
				data: {
					sourceType: FieldMappingSourceType.AI,
					sourceField: aiConfigTarget.label,
					aiConfig: config,
				},
			}).unwrap();
		}

		setAiConfigTarget(null);
	};

	const handleSaveNewMapping = async (mapping: NewPipedriveMapping) => {
		if (
			!mapping.pipedriveField ||
			(mapping.sourceType !== FieldMappingSourceType.AI && !mapping.sourceField) ||
			(mapping.sourceType === FieldMappingSourceType.AI && !mapping.aiConfig?.prompt)
		) {
			return;
		}

		setSavingTempId(mapping.tempId);

		try {
			await createMapping({
				sourceField: mapping.sourceField || mapping.pipedriveField,
				sourceType: mapping.sourceType,
				pipedriveField: mapping.pipedriveField,
				entityType: mapping.entityType as PipedriveEntityType,
				isActive: mapping.isActive,
				aiConfig: mapping.aiConfig,
			}).unwrap();
			setNewMappings((prev) => prev.filter((m) => m.tempId !== mapping.tempId));
		} catch (error) {
			console.error("Failed to save mapping:", error);
		} finally {
			setSavingTempId(null);
		}
	};

	const handleCancelNewMapping = (tempId: string) => {
		setNewMappings((prev) => prev.filter((m) => m.tempId !== tempId));
	};

	const handleToggleActive = async (id: number, isActive: boolean) => {
		await updateMapping({ id, data: { isActive } });
	};

	const handleDeleteMapping = async (id: number) => {
		await deleteMapping(id);
	};

	const tabItems = [
		{ key: Tabs.LEADS, label: ts("leads") },
		{ key: Tabs.PERSONS, label: ts("persons") },
		{ key: Tabs.ORGANIZATIONS, label: ts("organizations") },
		{ key: Tabs.DEALS, label: ts("deals") },
	];

	const renderTabContent = (tab: Tabs) => {
		const mappings = getMappingsForTab(tab);
		const pipedriveFields = getPipedriveFieldsForTab(tab);
		const srcFields = getSourceFieldsForTab(tab);
		const tabNewMappings = newMappings.filter((m) => m.entityType === TAB_ENTITY_MAP[tab]);

		return (
			<div className="mt-2">
				<div className="flex items-center gap-3 py-3 border-b border-border-light font-medium text-sm text-text-meta">
					<div className="flex-1">{ts("ourField")}</div>
					<div className="w-[24px]"></div>
					<div className="flex-1">{ts("pipedriveField")}</div>
					<div className="w-[60px] text-center">{ts("active")}</div>
					<div className="w-[80px]"></div>
				</div>

				{isLoading ? (
					<LoadingBlock className="py-8" />
				) : (
					<>
						{mappings?.map((mapping) => (
							<PipedriveMappingRow
								key={mapping.id}
								mapping={mapping}
								pipedriveFields={pipedriveFields}
								sourceFields={srcFields}
								ts={ts}
								onConfigureAi={(m) => handleConfigureAi(m, pipedriveFields)}
								onDelete={handleDeleteMapping}
								onToggleActive={handleToggleActive}
							/>
						))}
						{tabNewMappings.map((mapping) => (
							<PipedriveNewMappingRow
								key={mapping.tempId}
								isSaving={savingTempId === mapping.tempId}
								mapping={mapping}
								pipedriveFields={pipedriveFields}
								sourceFields={srcFields}
								ts={ts}
								usedPipedriveFieldsByType={usedPipedriveFieldsByType}
								usedSourceFields={usedSourceFields}
								onCancel={handleCancelNewMapping}
								onConfigureAi={(m) => handleConfigureAi(m, pipedriveFields)}
								onSave={handleSaveNewMapping}
								onUpdate={handleUpdateNewMapping}
							/>
						))}
						{!mappings?.length && !tabNewMappings.length && (
							<div className="py-6 text-center text-text-meta">{ts("noMappings")}</div>
						)}
					</>
				)}

				<div className="mt-4 flex flex-wrap items-center gap-2">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="w-[16px] h-[16px] text-text mr-2" icon={faPlus} />}
						size={ButtonSize.S}
						title={ts("addMapping")}
						onClick={handleAddMapping}
					/>
					<PipedriveAddDefaultsButton entityType={entityType} ts={ts} />
				</div>

				{(() => {
					const aiCount =
						(mappings?.filter((m) => m.sourceType === FieldMappingSourceType.AI).length ?? 0) +
						tabNewMappings.filter((m) => m.sourceType === FieldMappingSourceType.AI).length;
					const mappedCount = (mappings?.length ?? 0) + tabNewMappings.length;

					return (
						<FieldMappingFooter
							aiCount={aiCount}
							creditPillLabel={ts("footerCreditPill")}
							mappedCount={mappedCount}
							summaryLabel={ts("footerSummary", { mapped: mappedCount, ai: aiCount })}
							testIdPrefix="pipedrive"
						/>
					);
				})()}
			</div>
		);
	};

	return (
		<Modal handleClose={onClose} isOpened={isOpen} size="lg" title={ts("title")}>
			<div className="p-4">
				<div className="flex border-b border-border-light mb-2">
					{tabItems.map((tab) => (
						<button
							key={tab.key}
							className={classNames(
								"px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer",
								currentTab === tab.key
									? "border-brand-light text-brand-light"
									: "border-transparent text-text-meta hover:text-text hover:border-border-light"
							)}
							type="button"
							onClick={() => setCurrentTab(tab.key)}
						>
							{tab.label}
						</button>
					))}
				</div>
				{renderTabContent(currentTab)}
			</div>
			<AiConfigModal
				allFields={aiConfigTarget?.allFields ?? []}
				callbacks={{
					onCancelSearchProspects: handleCancelAiProspectSearch,
					onSearchProspects: handleSearchAiProspects,
					onRunTest: (prospectId, prompt, fallbackValue) =>
						testAiMapping({
							prospectId,
							entityType: aiConfigTarget?.entityType ?? entityType,
							pipedriveField: aiConfigTarget?.targetField.name,
							prompt,
							allowedValues: aiConfigTarget?.targetField.options?.map((option) => option.value),
							fallbackValue,
						}).unwrap(),
				}}
				initialConfig={aiConfigTarget?.aiConfig}
				isOpen={Boolean(aiConfigTarget)}
				labels={buildAiConfigLabels(ts)}
				subtitle={ts("aiConfigSubtitle", { label: aiConfigTarget?.label ?? "" })}
				targetField={aiConfigTarget?.targetField}
				title={ts("aiConfigTitle")}
				onClose={() => setAiConfigTarget(null)}
				onSave={handleSaveAiConfig}
			/>
		</Modal>
	);
};
